/**
 * Make `localStorage` usable for the life of the page, even when the browser
 * will not provide it.
 *
 * WHY. The app reads and writes localStorage in well over a hundred places,
 * unguarded: the session token, the user id, the store slug, router guards,
 * the Vuex login/logout actions. Some browsers a shopper scans a QR code with
 * do not give us working storage at all:
 *
 *   - Safari / managed Chrome with site data blocked: touching storage throws
 *     a SecurityError.
 *   - An in-app QR-scanner WebView that did not enable DOM storage:
 *     `window.localStorage` is null.
 *   - Older private modes: a zero quota, so every setItem throws.
 *
 * Any one of those threw inside the storefront session gate and the visitor
 * saw "Store not found" for a store that was there (prod, 2026-10-01).
 *
 * WHAT. Probe storage once at boot. If it works, leave it alone -- this
 * function is then a no-op. If it does not, install an in-memory Storage in
 * its place, so every existing caller keeps working for the page lifetime.
 * The session token, the cart account, the anonymous markers all live in
 * memory; the cart itself is server-side on the account, so nothing a shopper
 * cares about is lost until they close the tab.
 *
 * ONE mechanism at the root rather than a guarded wrapper at each call site:
 * a wrapper only protects the call sites converted to it, and the ones left
 * behind (router guards, store actions) would still throw on the same browser.
 *
 * Must run before anything else touches storage (top of main.js).
 */

const PROBE_KEY = "__tf_storage_probe__";

function storageWorks(win) {
    try {
        const storage = win.localStorage;
        if (!storage || typeof storage.setItem !== "function") return false;
        storage.setItem(PROBE_KEY, "1");
        const ok = storage.getItem(PROBE_KEY) === "1";
        storage.removeItem(PROBE_KEY);
        return ok;
    } catch {
        return false;
    }
}

export function createMemoryStorage() {
    const map = new Map();
    return {
        getItem: (key) => (map.has(String(key)) ? map.get(String(key)) : null),
        setItem: (key, value) => {
            map.set(String(key), String(value));
        },
        removeItem: (key) => {
            map.delete(String(key));
        },
        clear: () => map.clear(),
        key: (index) => Array.from(map.keys())[index] ?? null,
        get length() {
            return map.size;
        },
    };
}

/**
 * @param {object} win the global object (`window` in the app).
 * @returns {boolean} true when the in-memory fallback was installed.
 */
export function installStorageFallback(win) {
    if (!win || storageWorks(win)) return false;
    try {
        Object.defineProperty(win, "localStorage", {
            value: createMemoryStorage(),
            configurable: true,
            writable: true,
        });
        return true;
    } catch {
        // A browser that refuses the redefinition leaves us where we were;
        // the gate's own error handling still shows a retryable state rather
        // than "Store not found".
        return false;
    }
}
